import React from 'react';
import { 
  Users, GraduationCap, ClipboardCheck, FileText, 
  BookOpen, Star, MessageCircle, Clock, TrendingUp, 
  Info, Calendar, Megaphone, PlusCircle, CheckCircle2
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useData } from '../context/DataContext';
import { useLoading } from '../hooks/useLoading';
import { useNavigate } from 'react-router-dom';
import { Topbar } from '../components/Topbar';
import { Card, Button, Badge, Skeleton } from '../components/ui';
import { motion } from 'framer-motion';

export function ProfessorDashboard() {
  const { user } = useAuth();
  const data = useData();
  const navigate = useNavigate();
  const loading = useLoading(600);

  if (!user || user.type !== 'professor' || !data) return null;

  const { notices, allEnrollments, auditLogs } = data;

  // Filtrar dados baseados nos cursos do professor
  const profCourses = user?.courses ? user.courses.split(',').map(c => c.trim().toLowerCase()).filter(c => c !== '') : [];
  
  const myStudents = (allEnrollments || []).filter(e => {
    if (!e || (!e.course && !e.courseName)) return false;
    const courseName = (e.course || e.courseName).toLowerCase();
    return profCourses.length === 0 || profCourses.includes(courseName);
  });

  const myClasses = [
    { name: 'Turma A', students: myStudents.filter(s => s?.turma === 'A').length, next: 'Segunda, 19:00', room: 'Lab 101' },
    { name: 'Turma B', students: myStudents.filter(s => s?.turma === 'B').length, next: 'Quarta, 20:30', room: 'Lab 203' },
  ];

  // Mock data for professor dashboard
  const stats = [
    ['Turmas Ativas', profCourses.length || '2', 'Gestão institucional', BookOpen, 'purple'],
    ['Total de Alunos', myStudents.length.toString(), 'Estudantes matriculados', Users, 'blue'],
    ['Avaliações Pendentes', '12', 'Para correção', Star, 'red'],
    ['Média das Turmas', '8.2', '+0.4 este mês', TrendingUp, 'green'],
  ];

  const filteredNotices = (notices || []).filter(n => 
    n.type === 'professor' || n.authorId === (user.enrollmentId || user.email)
  ).slice(0, 3);

  const userName = user?.name || 'Professor';

  return (
    <div className="dashboardWrapper">
      <Topbar />
      
      <section className="welcomeBanner bg-gradient-to-r from-blue/20 to-transparent p-8 rounded-[32px] border border-blue/20 mb-8">
        <h2 className="text-4xl font-black mb-2">Olá, Prof. {userName.split(' ')[0]}! 🎓</h2>
        <p className="text-lg text-muted max-w-2xl mb-6">
          Bem-vindo ao seu Painel Docente. Aqui você gerencia suas turmas, alunos, notas e atividades.
        </p>
        <div className="flex flex-wrap gap-4">
          <Button className="flex items-center gap-2 bg-[#5865F2] hover:bg-[#4752C4] border-none" onClick={() => window.open('https://discord.gg/lumenfall', '_blank')}>
            <MessageCircle size={18} /> Discord Oficial
          </Button>
          <Button variant="outline" className="flex items-center gap-2" onClick={() => navigate('/notas')}>
            <Star size={18} /> Lançar Notas
          </Button>
        </div>
      </section>

      <section className="stats">
        {stats.map(([title, value, sub, Icon, color], idx) => (
          <article className={`stat ${color}`} key={idx}>
            {loading ? (
              <div className="w-full">
                <Skeleton className="h-4 w-24 mb-2" />
                <Skeleton className="h-8 w-16 mb-2" />
                <Skeleton className="h-3 w-32" />
              </div>
            ) : (
              <>
                <div className="statIcon"><Icon size={30}/></div>
                <div>
                  <h3>{title}</h3>
                  <strong>{value}</strong>
                  <p>{sub}</p>
                </div>
              </>
            )}
          </article>
        ))}
      </section>

      <section className="bottomGrid">
        <div className="flex flex-col gap-8">
          <ProfessorClassesTable loading={loading} myStudents={myStudents} />
          <AuditLogsPanel logs={auditLogs} loading={loading} />
        </div>
        
        <div className="flex flex-col gap-6">
          <Card title="Próximas Aulas">
            <div className="mt-4 space-y-4">
              {myClasses.map((c, idx) => (
                <div key={idx} className="flex items-center gap-4 p-4 bg-white/5 rounded-2xl border border-white/5">
                  <div className="w-12 h-12 bg-purple/10 rounded-xl flex items-center justify-center text-purple">
                    <Clock size={24} />
                  </div>
                  <div>
                    <strong className="text-sm block">{c.name}</strong>
                    <p className="text-[10px] text-muted font-bold uppercase tracking-wider">{c.next} • {c.room}</p>
                  </div>
                </div>
              ))}
            </div>
          </Card>
          <QuickActionsPanel />
          <Card title="Comunicados Enviados">
            <div className="mt-4 space-y-4">
              {filteredNotices.length > 0 ? filteredNotices.map(n => (
                <div className="notice p-4 bg-white/5 rounded-2xl border border-white/5 hover:border-purple/30 transition-all cursor-pointer" key={n.id}>
                  <div className={`noticeIcon ${n.color} w-10 h-10 rounded-xl flex items-center justify-center mb-3`}><Megaphone size={18}/></div>
                  <div>
                    <strong className="text-sm block mb-1">{n.title}</strong>
                    <span className="text-xs text-muted line-clamp-2">{n.sub}</span>
                  </div>
                </div>
              )) : (
                <div className="text-center py-10 opacity-30">
                  <Megaphone size={40} className="mx-auto mb-2" />
                  <p className="text-xs font-bold uppercase">Sem novos avisos</p>
                </div>
              )}
            </div>
          </Card>
        </div>
      </section>
    </div>
  );
}

function QuickActionsPanel() {
  return (
    <Card title="Atalhos Docentes">
      <div className="grid grid-cols-2 gap-3 mt-4">
        <Button variant="outline" className="flex flex-col gap-2 h-auto py-4 border-white/5 hover:border-purple/30">
          <Calendar size={20} className="text-purple" />
          <span className="text-[10px] uppercase font-black">Calendário</span>
        </Button>
        <Button variant="outline" className="flex flex-col gap-2 h-auto py-4 border-white/5 hover:border-purple/30">
          <FileText size={20} className="text-purple" />
          <span className="text-[10px] uppercase font-black">Relatórios</span>
        </Button>
        <Button variant="outline" className="flex flex-col gap-2 h-auto py-4 border-white/5 hover:border-purple/30">
          <MessageCircle size={20} className="text-purple" />
          <span className="text-[10px] uppercase font-black">Mensagens</span>
        </Button>
        <Button variant="outline" className="flex flex-col gap-2 h-auto py-4 border-white/5 hover:border-purple/30">
          <Info size={20} className="text-purple" />
          <span className="text-[10px] uppercase font-black">Ajuda</span>
        </Button>
      </div>
    </Card>
  );
}

function ProfessorClassesTable({ loading, myStudents }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { calculateClassFrequency } = useData();
  
  const profCourses = user?.courses ? user.courses.split(',').map(c => c.trim()).filter(c => c !== '') : [];

  const classes = [];
  profCourses.forEach(courseName => {
    classes.push({ 
      name: `${courseName} - Turma A`, 
      course: courseName,
      turma: 'A',
      students: myStudents.filter(s => (s.course === courseName || s.courseName === courseName) && s.turma === 'A').length, 
      frequency: calculateClassFrequency(courseName, 'A'), 
      status: 'Ativa' 
    });
    classes.push({ 
      name: `${courseName} - Turma B`, 
      course: courseName,
      turma: 'B',
      students: myStudents.filter(s => (s.course === courseName || s.courseName === courseName) && s.turma === 'B').length, 
      frequency: calculateClassFrequency(courseName, 'B'), 
      status: 'Ativa' 
    });
  });

  return (
    <Card title="Gestão de Turmas" subtitle="Controle por Turma A e B" actions={<Button variant="outline" size="sm" onClick={() => navigate('/minhas-turmas')}>Ver Detalhes</Button>} className="tablePanel">
      <div className="tableWrap mt-4">
        {loading ? (
          <div className="space-y-4">
            {[1, 2].map(i => <Skeleton key={i} className="h-12 w-full" />)}
          </div>
        ) : classes.length > 0 ? (
          <table>
            <thead>
              <tr>
                <th>Turma</th>
                <th>Alunos</th>
                <th>Frequência</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {classes.map((c, idx) => (
                <tr key={idx}>
                  <td>
                    <div className="flex flex-col">
                      <strong className="text-sm">{c.name}</strong>
                      <span className="text-[10px] text-muted uppercase font-bold">Semestre 2026/1</span>
                    </div>
                  </td>
                  <td><Badge variant="blue">{c.students} Alunos</Badge></td>
                  <td><b className={`text-xs font-mono ${c.frequency >= 75 ? 'text-green' : 'text-red'}`}>{c.frequency}%</b></td>
                  <td>
                    <Button variant="secondary" size="sm" onClick={() => navigate('/meus-alunos')}>Ver Alunos</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="text-center py-10 opacity-30">
            <p className="text-xs font-bold uppercase">Nenhuma turma vinculada</p>
          </div>
        )}
      </div>
    </Card>
  );
}

function AuditLogsPanel({ logs, loading }) {
  return (
    <Card title="Auditoria de Ações" subtitle="Registros recentes de atividades sensíveis.">
      <div className="mt-4 space-y-3">
        {loading ? (
          <Skeleton className="h-32 w-full" />
        ) : logs.length > 0 ? (
          logs.slice(0, 5).map(log => (
            <div key={log.id} className="flex items-center justify-between p-3 bg-white/5 rounded-xl border border-white/5">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-purple/10 rounded-lg flex items-center justify-center text-purple">
                  <CheckCircle2 size={16} />
                </div>
                <div>
                  <p className="text-xs font-bold text-white">{log.action}</p>
                  <p className="text-[10px] text-muted">{log.user} • {log.date} às {log.time}</p>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="text-center py-6 border-2 border-dashed border-white/5 rounded-2xl">
            <p className="text-xs text-muted">Nenhum registro de auditoria encontrado.</p>
          </div>
        )}
      </div>
    </Card>
  );
}
